// frontend/src/components/admin/reseller/PackagePricingTable.js
import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  Paper,
  Typography,
  Box,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const PackagePricingTable = ({
  packages,
  selectedPackages,
  customPricing,
  onPackageSelect,
  onPriceChange,
  defaultMarkup,
  loading,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const calculateMarkupPrice = (basePrice) => {
    return parseFloat((basePrice * (1 + defaultMarkup / 100)).toFixed(2));
  };

  // Single price change handler
  const handlePriceChange = (packageCode, value) => {
    const newPrice = value === "" || isNaN(value) ? null : parseFloat(value);
    const newPricing = { ...customPricing };

    if (newPrice === null) {
      delete newPricing[packageCode];
    } else {
      newPricing[packageCode] = newPrice;
    }

    onPriceChange(newPricing);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentPackages = packages.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selectedPackages.length > 0 &&
                    selectedPackages.length < packages.length
                  }
                  checked={selectedPackages.length === packages.length}
                  onChange={(e) => {
                    onPackageSelect(
                      e.target.checked
                        ? packages.map((pkg) => pkg.packageCode)
                        : []
                    );
                  }}
                />
              </TableCell>
              <TableCell>{t("packages.name")}</TableCell>
              <TableCell>{t("packages.originalPrice")}</TableCell>
              <TableCell>{t("packages.markupPrice")}</TableCell>
              <TableCell>{t("packages.customPrice")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentPackages.map((pkg) => {
              const basePrice = pkg.price / 10000;
              const markupPrice = calculateMarkupPrice(basePrice);

              return (
                <TableRow key={pkg.packageCode}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedPackages.includes(pkg.packageCode)}
                      onChange={() => {
                        if (selectedPackages.includes(pkg.packageCode)) {
                          onPackageSelect(
                            selectedPackages.filter(
                              (code) => code !== pkg.packageCode
                            )
                          );
                        } else {
                          onPackageSelect([
                            ...selectedPackages,
                            pkg.packageCode,
                          ]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{pkg.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {pkg.packageCode}
                    </Typography>
                  </TableCell>
                  <TableCell>${basePrice.toFixed(2)}</TableCell>
                  <TableCell>${markupPrice.toFixed(2)}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={customPricing[pkg.packageCode] || ""}
                      onChange={(e) => {
                        handlePriceChange(pkg.packageCode, e.target.value);
                      }}
                      placeholder={`$${markupPrice.toFixed(2)}`}
                      InputProps={{
                        startAdornment: (
                          <Box component="span" mr={1}>
                            $
                          </Box>
                        ),
                      }}
                      helperText={
                        customPricing[pkg.packageCode]
                          ? t("packages.customPriceSet")
                          : t("packages.markupPriceUsed")
                      }
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={packages.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default PackagePricingTable;
